import type {
  Workflow,
  WorkflowEnrollment,
  WorkflowEnrollmentStatus,
  WorkflowFailureReason,
  WorkflowInput,
  WorkflowStatus,
  WorkflowTemplate,
} from "@sales-ops-amplify/shared";
import { apiGet, apiPatch, apiPost } from "./client";

export function listWorkflows(params?: {
  status?: WorkflowStatus | "all";
  q?: string;
}): Promise<{ workflows: Workflow[] }> {
  return apiGet(
    "/api/workflows",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function getWorkflow(
  workflowId: string,
): Promise<{ workflow: Workflow }> {
  return apiGet(`/api/workflows/${workflowId}`);
}

export function createWorkflow(
  input: WorkflowInput & { templateId?: string; aiPrompt?: string },
): Promise<{ workflow: Workflow }> {
  return apiPost("/api/workflows", input);
}

export function updateWorkflow(
  workflowId: string,
  patch: Partial<WorkflowInput> & { status?: WorkflowStatus },
): Promise<{ workflow: Workflow }> {
  return apiPatch(`/api/workflows/${workflowId}`, patch);
}

export function activateWorkflow(
  workflowId: string,
): Promise<{ workflow: Workflow }> {
  return apiPost(`/api/workflows/${workflowId}/activate`, {});
}

export function deactivateWorkflow(
  workflowId: string,
): Promise<{ workflow: Workflow }> {
  return apiPost(`/api/workflows/${workflowId}/deactivate`, {});
}

export function archiveWorkflow(
  workflowId: string,
): Promise<{ workflow: Workflow }> {
  return apiPost(`/api/workflows/${workflowId}/archive`, {});
}

export function cloneWorkflow(
  workflowId: string,
): Promise<{ workflow: Workflow }> {
  return apiPost(`/api/workflows/${workflowId}/clone`, {});
}

export function listWorkflowTemplates(): Promise<{
  templates: WorkflowTemplate[];
}> {
  return apiGet("/api/workflows/templates");
}

export function aiDraftWorkflow(
  prompt: string,
): Promise<{ draft: WorkflowInput }> {
  return apiPost("/api/workflows/ai-draft", { prompt });
}

export function listWorkflowEnrollments(
  workflowId: string,
  params?: { status?: WorkflowEnrollmentStatus },
): Promise<{ enrollments: WorkflowEnrollment[] }> {
  return apiGet(
    `/api/workflows/${workflowId}/enrollments`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function enrollInWorkflow(
  workflowId: string,
  body: {
    leadIds?: string[];
    accountIds?: string[];
    apolloPersonIds?: string[];
  },
): Promise<{ enrollments: WorkflowEnrollment[]; skipped: string[] }> {
  return apiPost(`/api/workflows/${workflowId}/enroll`, body);
}

export function retryWorkflowEnrollments(
  workflowId: string,
  body?: { enrollmentIds?: string[]; reason?: WorkflowFailureReason },
): Promise<{ retried: number; enrollments: WorkflowEnrollment[] }> {
  return apiPost(`/api/workflows/${workflowId}/enrollments/retry`, body ?? {});
}

export function processDueWorkflows(): Promise<{
  scheduleEnrolled: number;
  resumed: number;
}> {
  return apiPost("/api/workflows/process-due", {});
}
